'use client';

// components/WebBrowsingSegment.tsx
// ChatGPT/Claude-style web-browsing telemetry, deliberately CONTAINER-LESS.
//   • While searching: a single subtle status line ("Searching the web…").
//   • When done: a clean inline row of clickable source favicon chips.
// No bordered box, no collapsible chevron. Styling uses explicit gray HEX
// (project pref), not low-opacity white.
//
// Presentational only: it takes a WebBrowsingState. Chat.tsx builds that state
// from the persisted WEB_SEARCH command results (buildWebBrowsingFromScripts).

import { memo, useEffect, useMemo, useState } from 'react';
import { domainFromUrl, googleFaviconUrl, duckDuckGoFaviconUrl } from '@/lib/web/favicon';

import type { WebBrowsingState } from '@/lib/web/webBrowsing';

/* ------------------------------- tiny glyphs ------------------------------- */

const GlobeGlyph = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#6E6E6E"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18" />
  </svg>
);

// Telemetry lines are 14px #8E8E8E with a 14px glyph, the same as the command trace in Chat.tsx.
const SearchGlyph = ({ color = '#8E8E8E' }: { color?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0">
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

/* ------------------------------- favicon (img) ------------------------------ */
// google s2 -> duckduckgo -> globe glyph, via onError stage advance.

const Favicon = memo(({ url, size = 14 }: { url: string; size?: number }) => {
  const domain = useMemo(() => domainFromUrl(url), [url]);
  const [stage, setStage] = useState<0 | 1 | 2>(0);
  if (!domain || stage === 2) return <GlobeGlyph size={size} />;
  const src = stage === 0 ? googleFaviconUrl(domain, 64) : duckDuckGoFaviconUrl(domain);
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      onError={() => setStage((s) => (s + 1) as 0 | 1 | 2)}
      className="shrink-0 rounded-[3px] bg-[#2A2A2A] object-cover"
      style={{ width: size, height: size }}
    />
  );
});
Favicon.displayName = 'Favicon';

/* ------------------------------- main segment ------------------------------ */

export const WebBrowsingSegment = memo(({ state }: { state: WebBrowsingState }) => {
  const { phase, queries = [], activeUrl, sources = [], neverRan = false } = state;
  const isDone = phase === 'done';
  const activeDomain = activeUrl ? domainFromUrl(activeUrl) : null;

  // Elapsed since this segment appeared. A grounded search takes ~20s and the agent fires several,
  // so a run is often minutes long, and reported as "he took too much time, we don't see what he
  // is doing". A ticking number is the difference between working and hung.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (isDone) return;
    const started = Date.now();
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [isDone]);

  // ---- Searching: one plain, borderless telemetry line ----
  if (!isDone) {
    const latestQuery = queries.length ? queries[queries.length - 1] : null;
    const label = activeDomain ? `Reading ${activeDomain}` : 'Searching the web';
    return (
      <div className="my-1 flex items-center gap-2" style={{ fontSize: 14, lineHeight: '20px' }}>
        <style dangerouslySetInnerHTML={{ __html: '@keyframes wbShim{0%{background-position:200% 0}100%{background-position:-200% 0}}.wb-shim{background:linear-gradient(90deg,#6E6E6E 0%,#E8E8E8 50%,#6E6E6E 100%);background-size:200% 100%;-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;color:transparent;animation:wbShim 1.6s linear infinite}' }} />
        {activeDomain ? <Favicon url={activeUrl!} size={14} /> : <SearchGlyph />}
        <span className="wb-shim truncate" style={{ fontSize: 'inherit' }}>
          {label}
          {/* Which search of how many, so several queries do not look like one stuck query. */}
          {queries.length > 1 ? ` (${queries.length})` : ''}
          {latestQuery && !activeDomain ? ` · “${latestQuery}”` : ''}…
        </span>
        {/* Outside the shimmer so it stays readable, and only once it is worth mentioning. */}
        {elapsed >= 3 ? (
          <span className="shrink-0 tabular-nums" style={{ color: '#8E8E8E', fontSize: 'inherit' }}>
            {elapsed}s{sources.length > 0 ? ` · ${sources.length} sources` : ''}
          </span>
        ) : null}
      </div>
    );
  }

  // ---- Done: one muted line saying what the search actually produced. ----
  // Citations still live inline in the answer as source pills, so this is NOT a "Sources" recap;
  // it is the receipt. Rendering nothing meant a search that found NOTHING looked exactly like a
  // search that found plenty, and the user could not tell whether the answer was grounded.
  if (queries.length === 0 && sources.length === 0) return null;
  return (
    <div className="my-1 flex items-center gap-2" style={{ color: '#8E8E8E', fontSize: 14, lineHeight: '20px' }}>
      <SearchGlyph />
      <span className="truncate" style={{ fontSize: 'inherit' }}>
        {queries.length > 0
          ? `${queries.length} ${queries.length === 1 ? 'search' : 'searches'}`
          : 'Searched'}
        {' · '}
        {sources.length > 0
          ? `${sources.length} ${sources.length === 1 ? 'source' : 'sources'}`
          : neverRan
            // NOT "found nothing". These searches were never run in the snapshot being rendered, so
            // claiming an empty result asserts a failure that never happened.
            ? 'sources not recorded'
            : 'no sources found'}
      </span>
    </div>
  );
});
WebBrowsingSegment.displayName = 'WebBrowsingSegment';
